<template>
    <grid
        :fetchCallback="fetchCallback"
        :columnDefs="columnDefs"
        :suppressScrollOnNewData="true"
        :maxBlocksInCache="null"
        ref="grid"
        class="nextpax-airbnb-vrbo-grid"
        :isSearch="false"
        :isAutoWidth="true"
        :headerHeight="45"
        :pinnedBottomRowData="pinnedBottomRowData"
        :getRowStyle="rowStyle"
        gridTheme="ag-theme-alpine-report"
        @on-total-records="setTotalRecords"
    >
        <busy-modal :show="busy"></busy-modal>
        <date-range-picker
            :range-options="predefinedRanges"
            :value="datesValue"
            :date-format="userUnicodeDateFormat"
            @input="onDaterangeChange"
        ></date-range-picker>
        <responsive-dropdown :icon="['fas', 'filter']" :items="reportOnOptions" variant="primary" v-if="isNextpax">
            {{ reportLabels[reportOn] }}
        </responsive-dropdown>
        <responsive-button
            variant="primary"
            :href="excelHref"
            @click="generatingExcelReportSuccessToast"
            :icon="['fas', 'file-excel']"
        ></responsive-button>
        <responsive-button variant="primary" @click="refresh" :icon="['fas', 'arrows-rotate']"
            >Refresh</responsive-button
        >
    </grid>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';

import DateRangePicker, {
    nameToRange,
    TODAY,
    YESTERDAY,
    THIS_MONTH,
    LAST_MONTH,
    THIS_YEAR,
    LAST_YEAR,
    ALL_DATES,
} from '../../../generic/date-range-picker/date-range-picker.vue';
import format from 'date-fns/format';
import grid from '../../../generic/grid/grid.vue';
import { mapGetters } from 'vuex';
import responsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../../generic/responsive-dropdown/responsive-dropdown.vue';
import MultiLineHeader from '../../../common/multi-line-header.vue';

const SERVER_DATE_FORMAT = 'yyyy-MM-dd';
const REPORT_ON_INVOICE_START = 'INVOICE_START';
const REPORT_ON_INVOICE_END = 'INVOICE_END';
const reportLabels = {
    INVOICE_START: 'Invoice Start Date',
    INVOICE_END: 'Invoice End Date',
};

export default {
    components: {
        BusyModal,
        grid,
        responsiveButton,
        ResponsiveDropdown,
        DateRangePicker,
        multiLineHeader: MultiLineHeader,
    },
    props: {
        url: {
            type: String,
            default: '',
        },
        isNextpax: {
            type: Boolean,
            default: false,
        },
        isVrbo: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const defaultDates = this.$store.getters['reportFeeServicesLocalStorage/dateRange'];
        return {
            busy: null,
            offset: 0,
            limit: 20,
            sort: 'created',
            desc: false,
            search: '',
            dateRequestFormat: 'yyyy-MM-dd',
            defaultDates,
            dates: null,
            predefinedRanges: [TODAY, YESTERDAY, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR, ALL_DATES],
            total: null,
            rowStyle: null,
            totalRecords: 0,
            reportOn: REPORT_ON_INVOICE_START,
            reportLabels,
        };
    },
    beforeMount() {
        this.rowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', '']),
        columnDefs() {
            return this.isNextpax
                ? [
                      {
                          headerName: '#',
                          field: 'number',
                          minWidth: 50,
                          sortable: false,
                          colSpan: (params) => (params.node.rowPinned ? 3 : 1),
                          valueGetter: (params) => {
                              return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                          },
                      },
                      {
                          field: 'created',
                          headerName: 'Created',
                          type: 'dateColumn',
                          sort: 'desc',
                          minWidth: 90,
                      },
                      {
                          field: 'partner',
                          headerName: 'Partner',
                          minWidth: 90,
                      },
                      {
                          field: 'from_date',
                          headerName: 'From Date',
                          type: 'dateColumn',
                          minWidth: 90,
                      },
                      {
                          field: 'to_date',
                          headerName: 'To Date',
                          type: 'dateColumn',
                          minWidth: 90,
                      },
                      {
                          field: 'state',
                          headerName: 'I.Status',
                          minWidth: 90,
                      },
                      {
                          field: 'status',
                          headerName: 'C.Status',
                          minWidth: 90,
                      },
                      {
                          field: 'meta_res',
                          headerName: 'Invoice #',
                          minWidth: 90,
                      },
                      {
                          field: 'subscriber',
                          headerName: 'Subscriber',
                          minWidth: 90,
                      },
                      {
                          field: 'guest_name',
                          headerName: 'Guest Name',
                          minWidth: 120,
                      },
                      {
                          field: 'remote_id',
                          headerName: 'Remote #ID',
                          sortable: false,
                          minWidth: 90,
                      },
                      {
                          field: 'rental_charges',
                          headerName: 'Rental Charges',
                          sortable: false,
                          minWidth: 100,
                          type: 'amountColumn',
                          headerClass: 'text-right',
                      },
                      {
                          field: 'total_fee',
                          headerName: 'Total Fees',
                          sortable: false,
                          minWidth: 90,
                          type: 'amountColumn',
                          headerClass: 'text-right',
                      },
                      {
                          field: 'partner_fee',
                          sortable: false,
                          minWidth: 90,
                          type: 'amountColumn',
                          headerName: 'Partner (1% / USD)',
                          headerComponent: 'multiLineHeader',
                          headerComponentParams: {
                              items: ['Partner', '(1% / USD)'],
                              itemClass: 'text-right',
                          },
                      },
                      {
                          field: 'lodgix_fee',
                          sortable: false,
                          minWidth: 90,
                          type: 'amountColumn',
                          headerName: 'Lodgix (1% / USD)',
                          headerComponent: 'multiLineHeader',
                          headerComponentParams: {
                              items: ['Lodgix', '(1% / USD)'],
                              itemClass: 'text-right',
                          },
                      },
                      {
                          field: 'total_fee_usd',
                          headerName: 'Total Fees (USD)',
                          sortable: false,
                          minWidth: 100,
                          type: 'amountColumn',
                          headerClass: 'text-right',
                      },
                      {
                          field: 'exchange_rate',
                          headerName: 'Exchange Rate',
                          sortable: false,
                          minWidth: 90,
                      },
                      {
                          field: 'collect_date',
                          headerName: 'Collected',
                          sortable: false,
                          minWidth: 90,
                          type: 'dateColumn',
                          autoWidthExclude: true,
                      },
                  ]
                : [
                      {
                          headerName: '#',
                          field: 'number',
                          minWidth: 50,
                          sortable: false,
                          colSpan: (params) => (params.node.rowPinned ? 3 : 1),
                          valueGetter: (params) => {
                              return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                          },
                      },
                      {
                          field: 'created',
                          headerName: 'Created',
                          type: 'dateColumn',
                          sort: 'desc',
                          minWidth: 90,
                      },
                      {
                          field: 'partner',
                          headerName: 'Partner',
                          minWidth: 90,
                          sortable: false,
                      },
                      {
                          field: 'from_date',
                          headerName: 'From Date',
                          type: 'dateColumn',
                          minWidth: 90,
                      },
                      {
                          field: 'state',
                          headerName: 'I.Status',
                          minWidth: 90,
                      },
                      {
                          field: 'status',
                          headerName: 'C.Status',
                          minWidth: 90,
                      },
                      {
                          field: 'meta_res',
                          headerName: 'Invoice #',
                          minWidth: 90,
                      },
                      {
                          field: 'subscriber',
                          headerName: 'Subscriber',
                          minWidth: 90,
                      },
                      {
                          field: 'guest_name',
                          headerName: 'Guest Name',
                          minWidth: 120,
                      },
                      {
                          field: 'remote_id',
                          headerName: 'Remote #ID',
                          sortable: false,
                          minWidth: 90,
                      },
                      {
                          field: 'rental_charges',
                          headerName: 'Rental Charges',
                          sortable: false,
                          minWidth: 100,
                          type: 'amountColumn',
                          headerClass: 'text-right',
                      },
                      {
                          field: 'rental_charges_usd',
                          headerName: 'Rental Charges (USD)',
                          sortable: false,
                          minWidth: 110,
                          type: 'amountColumn',
                          headerClass: 'text-right',
                      },
                      {
                          field: 'lodgix_fee',
                          sortable: false,
                          minWidth: 100,
                          type: 'amountColumn',
                          headerName: 'Lodgix (0.5% / USD)',
                          headerComponent: 'multiLineHeader',
                          headerComponentParams: {
                              items: ['Lodgix', '(0.5% / USD)'],
                              itemClass: 'text-right',
                          },
                      },
                      {
                          field: 'total_fee_usd',
                          sortable: false,
                          type: 'amountColumn',
                          headerName: 'Total Fees (USD)',
                          headerComponent: 'multiLineHeader',
                          headerComponentParams: {
                              items: ['Total Fees', '(USD)'],
                              itemClass: 'text-right',
                          },
                          minWidth: 90,
                      },
                      {
                          field: 'exchange_rate',
                          headerName: 'Exchange Rate',
                          sortable: false,
                          minWidth: 100,
                      },
                      {
                          field: 'collect_date',
                          headerName: 'Collected',
                          sortable: false,
                          minWidth: 90,
                          type: 'dateColumn',
                          autoWidthExclude: true,
                      },
                  ];
        },
        fetchCallback() {
            return ({ offset, limit, sort, desc, search }) => {
                this.offset = offset;
                this.limit = limit;
                this.sort = sort;
                this.desc = desc;
                this.search = search;
                this.fetchTotals();

                var url = encodeURI(
                    `${this.url}?${this.startDateFieldName}=${this.startDateRequest}&${this.endDateFieldName}=${this.endDateRequest}`
                );

                return API.get(url, {
                    params: {
                        offset,
                        limit,
                        sort,
                        desc,
                        search,
                    },
                });
            };
        },
        reportOnOptions() {
            return [
                {
                    label: this.reportLabels[REPORT_ON_INVOICE_START],
                    click: () => {
                        this.reportOn = REPORT_ON_INVOICE_START;
                        this.refresh();
                    },
                },
                {
                    label: this.reportLabels[REPORT_ON_INVOICE_END],
                    click: () => {
                        this.reportOn = REPORT_ON_INVOICE_END;
                        this.refresh();
                    },
                },
            ];
        },
        excelHref() {
            return `/app/channel-commissions/admin/${
                this.isNextpax ? 'nextpax' : this.isVrbo ? 'vrbo' : 'airbnb'
            }/excel?offset=${this.offset}&limit=${this.limit}&sort=${this.sort}&desc=${this.desc}&search=${
                this.search
            }&${this.startDateFieldName}=${this.startDateRequest}&${this.endDateFieldName}=${this.endDateRequest}`;
        },
        pinnedBottomRowData() {
            return this.isNextpax
                ? [
                      {
                          number: this.totalRecords,
                          partner_fee: this.total?.partner_fee || 0,
                          lodgix_fee: this.total?.lodgix_fee || 0,
                          total_fee_usd: this.total?.total_fee_usd || 0,
                      },
                  ]
                : [
                      {
                          number: this.totalRecords,
                          rental_charges: this.total?.rental_charges || 0,
                          lodgix_fee: this.total?.lodgix_fee || 0,
                      },
                  ];
        },
        datesValue: {
            get() {
                if (!this.dates) {
                    this.dates = nameToRange(this.defaultDates.name);
                    if (!this.dates.from) {
                        this.dates = { ...this.defaultDates };
                    }
                }
                return this.dates;
            },
            set(val) {
                this.dates = val;
            },
        },
        startDateRequest() {
            return format(this.datesValue.from, SERVER_DATE_FORMAT);
        },
        endDateRequest() {
            return format(this.datesValue.to, SERVER_DATE_FORMAT);
        },
        startDateFieldName() {
            return this.reportOn === REPORT_ON_INVOICE_START ? 'from_date_start_date' : 'to_date_start_date';
        },
        endDateFieldName() {
            return this.reportOn === REPORT_ON_INVOICE_START ? 'from_date_end_date' : 'to_date_end_date';
        },
    },
    methods: {
        refresh() {
            this.$refs.grid.refreshDataSource();
        },
        setTotalRecords(value) {
            this.totalRecords = value;
        },
        onDaterangeChange(value) {
            this.datesValue = { ...value };
            this.$store.dispatch('reportFeeServicesLocalStorage/changeDateRange', value);
            this.refresh();
        },
        async fetchTotals() {
            try {
                this.busy = true;
                const { data } = await API.get(
                    `app/channel-commissions/admin/${
                        this.isNextpax ? 'nextpax' : this.isVrbo ? 'vrbo' : 'airbnb'
                    }/get-totals/?offset=${this.offset}&limit=${this.limit}&sort=${this.sort}&desc=${
                        this.desc
                    }&search=${this.search}&${this.startDateFieldName}=${this.startDateRequest}&${
                        this.endDateFieldName
                    }=${this.endDateRequest}`
                );
                this.total = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch totals' });
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.nextpax-airbnb-vrbo-grid {
    :deep(.ag-cell-value) {
        font-size: 13px;
    }
}
</style>
